import { useState } from 'react';
import { useOverrideVettingCheck, useDeclineVettingCheck } from '../hooks/useVettingActions';
import { VettingDialog } from './VettingDialog';

type VettingActionMode = 'override' | 'decline';

type VettingActionModalProps = {
  checkId: string;
  providerId: string;
  mode: VettingActionMode;
  open: boolean;
  onClose: () => void;
};

const COPY: Record<VettingActionMode, { title: string; description: string; noteRequired: boolean; cta: string }> = {
  override: {
    title: 'Record Override',
    description:
      'This override is recorded against your identity and retained for audit. It does not appear to the service provider.',
    noteRequired: true,
    cta: 'Submit Override',
  },
  decline: {
    title: 'Record Decline',
    description:
      'This will reject the provider (vetting status → Rejected) in the same action. The FCRA adverse-action process itself still runs separately in Checkr’s dashboard tooling.',
    noteRequired: false,
    cta: 'Record Decline & Reject',
  },
};

type ApiErrorShape = {
  message?: string;
  response?: { data?: { message?: string | string[] } };
};

export function getErrorMessage(error: unknown, fallback: string): string {
  const apiError = error as ApiErrorShape;
  const serverMessage = apiError.response?.data?.message;
  if (Array.isArray(serverMessage) && serverMessage.length > 0) return serverMessage.join('; ');
  if (typeof serverMessage === 'string' && serverMessage.length > 0) return serverMessage;
  if (typeof apiError.message === 'string' && apiError.message.length > 0) return apiError.message;
  return fallback;
}

/** Submits to the real override/decline endpoints — see hooks/useVettingActions.ts. */
export function VettingActionModal({ checkId, providerId, mode, open, onClose }: VettingActionModalProps) {
  const [note, setNote] = useState('');
  const copy = COPY[mode];
  const canSubmit = !copy.noteRequired || note.trim().length > 0;

  const overrideMutation = useOverrideVettingCheck();
  const declineMutation = useDeclineVettingCheck();
  const mutation = mode === 'override' ? overrideMutation : declineMutation;

  const handleSubmit = () => {
    if (mode === 'override') {
      overrideMutation.mutate(
        { checkId, providerId, note: note.trim() },
        { onSuccess: () => { setNote(''); onClose(); } },
      );
    } else {
      declineMutation.mutate(
        { checkId, providerId, note: note.trim() || undefined },
        { onSuccess: () => { setNote(''); onClose(); } },
      );
    }
  };

  return (
    <VettingDialog
      open={open}
      onClose={onClose}
      title={copy.title}
      description={copy.description}
      confirmLabel={copy.cta}
      pendingLabel="Submitting..."
      isPending={mutation.isPending}
      confirmDisabled={!canSubmit}
      onConfirm={handleSubmit}
      tone={mode === 'decline' ? 'danger' : 'primary'}
    >
      <div>
        <label htmlFor="vetting-action-note" className="mb-1 block text-sm font-medium text-gray-700">
          Note {copy.noteRequired && <span className="text-red-500">*</span>}
        </label>
        <textarea
          id="vetting-action-note"
          rows={4}
          placeholder={copy.noteRequired ? 'Note (required)' : 'Note (optional)'}
          value={note}
          onChange={(e) => setNote(e.target.value)}
          disabled={mutation.isPending}
          className="w-full rounded-md border border-gray-300 px-3 py-2 focus:border-transparent focus:outline-none focus:ring-2 focus:ring-[#026857] disabled:cursor-not-allowed disabled:bg-gray-100"
        />
      </div>
      {mutation.isError && (
        <p className="text-sm text-red-600">
          {getErrorMessage(mutation.error, 'Something went wrong. Please try again.')}
        </p>
      )}
    </VettingDialog>
  );
}
